/**
 * THE OFFERS A PRODUCT TAKES PART IN — quiet lines under its price.
 *
 * A card's price counts only what comes off one unit (`campaignLinePricing`).
 * What it does not show is named here, from `campaignOrderOffers`:
 *   - a once-per-order FIXED amount on the whole shop, a category or a
 *     collection ("₪40 off a watches order"), which never belongs in a unit's
 *     price — before, a ₪30 strap was priced "0 ₪" for it (UX-FINAL-11);
 *   - an offer that waits on a minimum spend, with that minimum in exact money
 *     ("15% off with an order over 199.90 ₪");
 *   - a price a campaign took to nothing, which reads as FREE, with the price it
 *     was — never a bare "0 ₪".
 * Every one of them, not only the first. Each line after the first says how it
 * relates to what comes before it: "plus" only when the checkout adds them up
 * (both "Allow stacking"), "or" when it takes the better one. Renders nothing
 * when no such offer reaches the product.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { campaignOrderOffers } from '../../utils/shopPromotions';
import { campaignNamesLabel } from '../../utils/campaignNamesLabel';
import { shopPrice } from '../../utils/currency';

export default function OrderOfferNote({ product, variant = null, campaigns, currency, className, testId }) {
  const { t } = useTranslation();
  const chosen = variant || (Array.isArray(product?.variants) ? product.variants[0] : null) || {};
  const offers = campaignOrderOffers(chosen, product || {}, campaigns || []);
  if (offers.length === 0) return null;
  const money = (n) => shopPrice(n, currency);

  const sentence = (offer) => {
    const name = offer.campaigns
      ? campaignNamesLabel(offer.campaigns.map((c) => c?.name), t)
      : offer.campaign?.name;
    if (offer.free && offer.minSpend == null) {
      return t('shop.freeOfferNote', { defaultValue: 'Free (instead of {{was}}) · {{name}}', was: money(offer.was), name });
    }
    if (offer.free) {
      return t('shop.itemFreeNoteOver', { defaultValue: 'Free with an order over {{min}} · {{name}}', min: money(offer.minSpend), name });
    }
    const amount = offer.percent != null ? `${offer.percent}%` : money(offer.amount);
    if (offer.minSpend == null) {
      return t('shop.orderOfferNote', { defaultValue: '{{amount}} off an order · {{name}}', amount, name });
    }
    if (offer.perUnit) {
      return t('shop.itemOfferNoteOver', {
        defaultValue: '{{amount}} off this item with an order over {{min}} · {{name}}',
        amount,
        min: money(offer.minSpend),
        name,
      });
    }
    return t('shop.orderOfferNoteOver', {
      defaultValue: '{{amount}} off an order over {{min}} · {{name}}',
      amount,
      min: money(offer.minSpend),
      name,
    });
  };
  const joined = (offer) => {
    if (!offer.joins) return sentence(offer);
    const word = offer.joins === 'plus'
      ? t('shop.offerPlus', { defaultValue: 'plus' })
      : t('shop.offerOr', { defaultValue: 'or' });
    return `${word} ${sentence(offer)}`;
  };

  return (
    <span className={className} data-testid={testId}>
      {offers.map((offer, i) => (
        <span
          key={`${offer.campaign?.id ?? offer.campaign?.name ?? 'offer'}-${i}`}
          data-offer-line
          style={offers.length > 1 ? { display: 'block' } : undefined}
        >
          {joined(offer)}
        </span>
      ))}
    </span>
  );
}
